import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  bxApiClient,
  onboardingApiClient,
  receiptApiClient,
} from "@shared/api/clients/apiClientFactory";
import { QueryStringParameters } from "@shared/api/url-builder";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

// ============================================================================
// STEP 1: Bill Cycle APIs
// ============================================================================

export const getReceiptBillTemplates = async (): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "receipt",
    "dashboard-cards",
    (query) => {
      query.push("remote_utility_id", REMOTE_UTILITY_ID);
      query.push("account_type_filter", "meter_replacement");

    }
  );
  const response = await receiptApiClient.get(url);
  return response.data?.result || response.data;
};

export const getBillingCyclesList = async (): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/cycles`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("remove_single_bills", 1);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const getBillingCycleDetails = async (
  cycleId: string | number,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billcycle/details/${cycleId}`,
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const getBillTemplatesList = async (): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/templates`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const saveBillingConfiguration = async (payload: any): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `create-bulk-bill`,
  );
  const response = await bxApiClient.post(url, payload);
  return response.data?.result;
};

export const draftBillingCycle = async (payload: any): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `create-bulk-bill`,
    (qs: QueryStringParameters) => {
      qs.push("draft", "true");
    },
  );
  const response = await bxApiClient.post(url, payload);
  return response.data?.result;
};

export const getDraftDetails = async (
  draftId: string | number,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `create-bulk-bill/${draftId}`,
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

// ============================================================================
// STEP 2: Fetch Consumer Data APIs
// ============================================================================

export const getDashboardSummary = async (
  cycleId: string | number,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `bulk-bill/dashboard/summary`,
    (qs: QueryStringParameters) => {
      qs.push("cycle_id", cycleId);
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const getConsumerAnalysis = async (
  cycleId: string | number,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/dashboard/consumer-analysis`,
    (qs: QueryStringParameters) => {
      qs.push("cycle_id", cycleId);
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const getConsumersList = async (
  cycleId: string | number,
  page: number = 1,
  limit: number = 50,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billcycle/consumers`,
    (qs: QueryStringParameters) => {
      qs.push("cycle_id", cycleId);
      qs.push("page", page);
      qs.push("limit", limit);
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data;
};

export const getBillingPlans = async (
  cycleId: string | number,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/plans`,
    (qs: QueryStringParameters) => {
      qs.push("cycle_id", cycleId);
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const getPlanDetails = async (planId: string | number): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    `plan-detail/${planId}`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("is_consumer_count", 1);
    },
  );
  const response = await onboardingApiClient.get(url);
  return response.data?.result;
};

export const getOutstandingAccounts = async (
  cycleId: string | number,
  page: number = 1,
  limit: number = 50,
  sortBy: string = "outstanding",
  sortOrder: string = "desc",
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/outstanding-accounts`,
    (qs: QueryStringParameters) => {
      qs.push("page", page);
      qs.push("limit", limit);
      qs.push("sort_by", sortBy);
      qs.push("sort_order", sortOrder);
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("cycle_id", cycleId);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data;
};

export const getPaymentDetails = async (
  cycleId: string | number,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/payment-details`,
    (qs: QueryStringParameters) => {
      qs.push("cycle_id", cycleId);
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const updateBillingConfiguration = async (
  billDraftId: string | number,
  payload: any,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `create-bulk-bill/${billDraftId}`,
  );
  const response = await bxApiClient.patch(url, payload);
  return response.data?.result;
};

// ============================================================================
// STEP 3: Meter Reading APIs
// ============================================================================

export const getMeterReadingSummary = async (
  cycleId: string | number,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/meter-readings/summary`,
    (qs: QueryStringParameters) => {
      qs.push("cycle_id", cycleId);
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const getMeterReadingAnalysis = async (
  cycleId: string | number,
): Promise<any> => {
  const remoteUtilityId = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/meter-readings/analysis`,
    (qs: QueryStringParameters) => {
      qs.push("cycle_id", cycleId);
      qs.push("remote_utility_id", remoteUtilityId);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const getMeterReadings = async (
  cycleId: string | number,
  draftId: string | number,

  filters?: {
    readingType?: string;
    search?: string;
    meterType?: string;
    service?: string;
    veeStatus?: string;
    status?: string;
    consumptionFlag?: string;
    page?: number;
    limit?: number;
  },
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/meter-readings`,
    (qs: QueryStringParameters) => {
      qs.push("page_number", filters.page);
      qs.push("limit", filters.limit);
      qs.push("cycle_id", cycleId);
      qs.push("draft_id", draftId);
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);

      if (filters) {
        if (filters.readingType) qs.push("reading_type", filters.readingType);
        if (filters.search) qs.push("search", filters.search);
        if (filters.meterType) qs.push("meter_type", filters.meterType);
        if (filters.service) qs.push("service", filters.service);
        if (filters.veeStatus) qs.push("vee_status", filters.veeStatus);
        if (filters.status) qs.push("status", filters.status);
        if (filters.consumptionFlag)
          qs.push("consumption_flag", filters.consumptionFlag);
      }
    },
  );
  const response = await bxApiClient.get(url);
  return response.data;
};

export const getMeterReplacementCases = async (
  cycleId: string | number,
  page: number,
  limit: number,
  search: string,
): Promise<any> => {
  const remoteUtilityId = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/meter-replacements`,
    (qs: QueryStringParameters) => {
      qs.push("page", page);
      qs.push("limit", limit);
      qs.push("cycle_id", cycleId);
      qs.push("remote_utility_id", remoteUtilityId);
      if (search) qs.push("search", search);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data;
};

export const getTrueUpSummary = async (
  cycleId: string | number,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/true-up/summary`,
    (qs: QueryStringParameters) => {
      qs.push("cycle_id", cycleId);
      qs.push("remote_utility_id", getRemoteUtilityId());
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const getTrueUpCases = async (
  cycleId: string | number,
  page: number,
  limit: number,
  search?: string,
  status?: string,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/true-up/cases`,
    (qs: QueryStringParameters) => {
      qs.push("page", page);
      qs.push("limit", limit);
      qs.push("cycle_id", cycleId);
      qs.push("remote_utility_id", getRemoteUtilityId());
      if (search) qs.push("search", search);
      if (status) qs.push("status", status);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data;
};

export const getBillDaysThresholdSummary = async (
  cycleId: string,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/bill-days-threshold/summary`,
    (qs: QueryStringParameters) => {
      qs.push("cycle_id", cycleId);
      qs.push("remote_utility_id", getRemoteUtilityId());
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const getBillDaysThresholdList = async (
  cycleId: string | number,
  page: number = 1,
  limit: number = 50,
  threshold?: string,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/bill-days-threshold`,
    (qs: QueryStringParameters) => {
      qs.push("page", page);
      qs.push("limit", limit);
      qs.push("cycle_id", cycleId);
      qs.push("remote_utility_id", getRemoteUtilityId());
      if (threshold) qs.push("threshold", threshold);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data;
};

export const estimateMeterReadings = async (payload: any): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/meter-readings/estimate`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", getRemoteUtilityId());
    },
  );
  const response = await bxApiClient.post(url, payload);
  return response.data;
};

export const excludeConsumersFromBilling = async (
  payload: any,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/meter-readings/exclude`,
  );
  const response = await bxApiClient.post(url, payload);
  return response.data?.result;
};

export const updateMeterConfiguration = async (
  billDraftId: string | number,
  payload: any,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `bulkbill/${billDraftId}`,
  );
  const response = await bxApiClient.patch(url, payload);
  return response.data?.result;
};

// ============================================================================
// STEP 4: Bill Generation APIs
// ============================================================================

export const startBillGeneration = async (
  billDraftId: string | number,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `bulk-bill-generation/${billDraftId}`,
  );
  const response = await bxApiClient.patch(url, { bill_generate: true });
  return response.data?.result;
};

export const trackBillGenerationProgress = async (
  taskId: string,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `track-bills-progress`,
    (qs: QueryStringParameters) => {
      qs.push("task_id", taskId);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const getBillCycleSummary = async (
  billCycleId: string | number,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `summary/${billCycleId}`,
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const getFailedBillsList = async (
  runId: string | number,
  page: number = 1,
  limit: number = 50,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/failed-bills`,
    (qs: QueryStringParameters) => {
      qs.push("run_id", runId);
      qs.push("page", page);
      qs.push("limit", limit);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data;
};

// ============================================================================
// STEP 5: Review Bills APIs
// ============================================================================

export const getBillingSummary = async (
  runId: string | number,
): Promise<any> => {
  const remoteUtilityId = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/summary`,
    (qs: QueryStringParameters) => {
      qs.push("run_id", runId);
      qs.push("remote_utility_id", remoteUtilityId);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const getBillsList = async (
  runId: string | number,
  page: number = 1,
  limit: number = 50,
  search?: string,
): Promise<any> => {
  const remoteUtilityId = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/bills`,
    (qs: QueryStringParameters) => {
      qs.push("page", page);
      qs.push("limit", limit);
      qs.push("run_id", runId);
      qs.push("remote_utility_id", remoteUtilityId);
      if (search) qs.push("search", search);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data;
};

export const getExcludedConsumers = async (
  runId: string | number,
  page: number = 1,
  limit: number = 50,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/excluded-consumers`,
    (qs: QueryStringParameters) => {
      qs.push("page", page);
      qs.push("limit", limit);
      qs.push("run_id", runId);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data;
};

export const getDistributionOverview = async (
  runId: string | number,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/distribution-overview`,
    (qs: QueryStringParameters) => {
      qs.push("run_id", runId);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const approveBills = async (
  runId: string | number,
  payload: any,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `create-bulk-bill/approve/${runId}`,
  );
  const response = await bxApiClient.put(url, payload);
  return response.data?.result;
};

export const rejectBills = async (
  runId: string | number,
  payload: any,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/${runId}/reject`,
  );
  const response = await bxApiClient.post(url, payload);
  return response.data?.result;
};

export const downloadBills = async (runId: string | number): Promise<Blob> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/${runId}/download-bills`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", getRemoteUtilityId());
    },
  );
  const response = await bxApiClient.get(url, {
    responseType: "blob",
  });
  return response.data;
};

export const getBillRunSummary = async (
  runId: string | number,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
    "bx",
    `billing/run-summary`,
    [runId],
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", getRemoteUtilityId());
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const getBillRunBillsList = async (
  runId: string | number,
  page: number = 1,
  limit: number = 50,
  search?: string,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
    "bx",
    `billing/${runId}`,
    ["bills"],
    (qs: QueryStringParameters) => {
      qs.push("page", page);
      qs.push("limit", limit);
      if (search) qs.push("search", search);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data;
};

export const getExcludedConsumersSummary = async (
  runId: string | number,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/${runId}/excluded-consumers`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", getRemoteUtilityId());
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const getExcludedConsumersList = async (
  runId: string | number,
  page: number = 1,
  limit: number = 50,
  search?: string,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/excluded-consumers`,
    (qs: QueryStringParameters) => {
      qs.push("run_id", runId);
      qs.push("page", page);
      qs.push("limit", limit);
      if (search) qs.push("search", search);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data;
};

export const fetchReadingsForExcludedConsumers = async (
  batchId: string | number,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/batches/${batchId}/excluded-consumers/fetch-readings`,

    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", getRemoteUtilityId());
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const generateBillsForExcludedConsumers = async (
  runId: string | number,
  payload: any,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/${runId}/excluded-consumers/generate-bills`,
  );
  const response = await bxApiClient.post(url, payload);
  return response.data?.result;
};

export const getActivityLog = async (runId: string | number): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/${runId}/activity-log`,
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const getServiceCharges = async (billCycleId: string): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `v1-billing/service-charges`,
    (qs: QueryStringParameters) => {
      qs.push("cycle_id", billCycleId);
      qs.push("remote_utility_id", getRemoteUtilityId());
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const getBillRuns = async (
  filters: Record<string, any>,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/batches`,
    (qs: QueryStringParameters) => {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          qs.push(key, value);
        }
      });
    },
  );
  const response = await bxApiClient.get(url);
  return response.data;
};

export const getRunDetails = async (runId: string): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/run/${runId}/details`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const getExportBillData = async (runId: string): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billing/${runId}/export-bill-data`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data;
};

export const retryGenerateBills = async (
  payload: any,
  scheduleId: string,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `retry-bills/${scheduleId}`,
  );
  const response = await bxApiClient.put(url, payload);
  return response.data?.result;
};

export const trackBillsProgress = async (taskId: string): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `track-bills-progress`,
    (qs: QueryStringParameters) => {
      qs.push("task_id", taskId);
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};
